import { ReactNode } from 'react';
import { Icon } from '@iconify-icon/react';

export type DocsCalloutVariant = 'note' | 'tip' | 'warning' | 'danger' | 'success';

interface DocsCalloutProps {
  variant?: DocsCalloutVariant;
  /** Optional bold heading. When omitted the body sits alone next to the icon. */
  title?: string;
  children: ReactNode;
}

const VARIANTS: Record<
  DocsCalloutVariant,
  { icon: string; wrapper: string; iconColor: string; title: string; body: string }
> = {
  note: {
    icon: 'tabler:info-circle',
    wrapper: 'border-slate-200 bg-slate-50',
    iconColor: 'text-slate-500',
    title: 'text-slate-900',
    body: 'text-slate-700',
  },
  tip: {
    icon: 'tabler:bulb',
    wrapper: 'border-primary-100 bg-primary-50',
    iconColor: 'text-primary-600',
    title: 'text-primary-900',
    body: 'text-primary-900/80',
  },
  warning: {
    icon: 'tabler:alert-triangle',
    wrapper: 'border-amber-200 bg-amber-50',
    iconColor: 'text-amber-600',
    title: 'text-amber-900',
    body: 'text-amber-900/80',
  },
  danger: {
    icon: 'tabler:alert-octagon',
    wrapper: 'border-rose-200 bg-rose-50',
    iconColor: 'text-rose-600',
    title: 'text-rose-900',
    body: 'text-rose-900/80',
  },
  success: {
    icon: 'tabler:circle-check',
    wrapper: 'border-emerald-200 bg-emerald-50',
    iconColor: 'text-emerald-600',
    title: 'text-emerald-900',
    body: 'text-emerald-900/80',
  },
};

/**
 * Standard aside for tips, caveats and warnings.
 *
 * Replaces the five different hand-rolled callout shapes the articles grew
 * (left-border strips, filled cards, icon circles) with one consistent block.
 */
const DocsCallout: React.FC<DocsCalloutProps> = ({ variant = 'note', title, children }) => {
  const styles = VARIANTS[variant];

  return (
    <aside className={`not-prose my-6 rounded-xl border ${styles.wrapper} p-4 sm:p-5`}>
      <div className="flex gap-3">
        <Icon
          icon={styles.icon}
          className={`mt-0.5 flex-shrink-0 text-lg ${styles.iconColor}`}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          {title && <p className={`mb-1 text-sm font-semibold ${styles.title}`}>{title}</p>}
          <div className={`text-sm leading-relaxed ${styles.body} [&_a]:underline [&_p+p]:mt-2`}>
            {children}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default DocsCallout;
